import {
  Card,
  CardBody,
  CardFooter,
  CardHeader,
  Flex,
  Icon,
  Skeleton,
  Spinner,
  Text,
} from '@chakra-ui/react';
import { EyeIcon, EyeOffIcon } from 'lucide-react';
import { useState } from 'react';
import { NewText } from '~/common/components/NewText/NewText';
import { useIsMobile } from '~/common/hooks/useIsMobile';
import { useBuyerOfferAnalysisContext } from '../Context/BuyerOfferAnalysisContext';
import { OfferScoreDetailsPopover } from './OfferScoreDetailsPopover';
import { OfferScoreGauge } from './OfferScoreGauge';

interface OfferScoreCardProps {
  size: 'sm' | 'md' | 'lg';
}

export const OfferScoreCard = ({ size }: OfferScoreCardProps) => {
  const isMobile = useIsMobile();
  const [isCardHovered, setIsCardHovered] = useState(false);
  const [isIconHovered, setIsIconHovered] = useState(false);
  const {
    listing,
    offerRecs,
    buyerOfferAnalysis,
    updateHideOfferScore,
    isUpdatingBuyerOfferAnalysis,
    isViewOnly,
  } = useBuyerOfferAnalysisContext();

  const competitiveness = offerRecs?.competitiveness;
  const isHidden = buyerOfferAnalysis?.hideOfferScore;

  if (!listing) {
    return (
      <Skeleton
        flexBasis={0}
        flexGrow={1}
        height={{ base: 'auto', xl: '250px' }}
        minWidth={{ base: 'auto', xl: '230px' }}
      />
    );
  }

  return (
    <Card
      bg={isHidden ? 'transparent' : undefined}
      border={isHidden ? '1px dashed' : undefined}
      borderColor={isHidden ? 'whiteAlpha.300' : undefined}
      data-testid="offer-score-card"
      flexBasis={0}
      flexGrow={1}
      height={{ base: 'auto', xl: '250px' }}
      minWidth={{ base: 'auto', xl: '230px' }}
      onMouseEnter={() => setIsCardHovered(true)}
      onMouseLeave={() => setIsCardHovered(false)}
    >
      <CardHeader
        filter={isHidden ? 'opacity(0.5)' : undefined}
        justifyContent="center"
      >
        <Flex alignItems="center" gap={1} justifyContent="center" width="100%">
          <Text color="whiteAlpha.600" textAlign="center">
            Offer Score
          </Text>
          <NewText text="Beta" />
        </Flex>
      </CardHeader>
      <CardBody filter={isHidden ? 'opacity(0.3)' : undefined}>
        <Flex alignItems="center" height="100%" justifyContent="center">
          <OfferScoreGauge size={size} />
        </Flex>
      </CardBody>
      <CardFooter visibility={isHidden ? 'hidden' : undefined}>
        <Flex alignItems="center" gap={1} justifyContent="center" width="100%">
          <OfferScoreDetailsPopover competitiveness={competitiveness?.rating} />
        </Flex>
      </CardFooter>
      {(isCardHovered || isMobile) && !isViewOnly && (
        <Flex
          alignItems="center"
          bg={isUpdatingBuyerOfferAnalysis ? 'black' : '#2B2B2B'}
          borderRadius="full"
          boxShadow="-3px -3px 7px 0px rgba(0, 0, 0, 0.25)" //TODO: move to theme
          cursor="pointer"
          data-testid="show-hide-offer-score-card-button"
          gap={2}
          mr={-2}
          mt={-2}
          overflow="hidden"
          p={2}
          position="absolute"
          right={0}
          textOverflow="clip"
          top={0}
          transition="all 0.2s"
          whiteSpace="nowrap"
          width={
            isIconHovered && !isMobile ? (isHidden ? '150px' : '160px') : '32px'
          }
          onClick={() => {
            if (isUpdatingBuyerOfferAnalysis) {
              return;
            }
            updateHideOfferScore(!isHidden);
          }}
          onMouseEnter={() => setIsIconHovered(true)}
          onMouseLeave={() => setIsIconHovered(false)}
        >
          {isUpdatingBuyerOfferAnalysis ? (
            <Spinner size="sm" />
          ) : (
            <Icon as={isHidden ? EyeIcon : EyeOffIcon} />
          )}
          {isIconHovered && !isMobile && (
            <Text fontSize="sm" fontWeight="medium">
              {isHidden ? 'Show in' : 'Hide from'} buyer view
            </Text>
          )}
        </Flex>
      )}
      {isHidden && !isViewOnly && (
        <Flex
          alignSelf="center"
          background="opaque.indigo_800_whiteAlpha_100"
          border="1px solid"
          borderColor="whiteAlpha.100"
          borderRadius="8px"
          bottom={3}
          p={1}
          position="absolute"
        >
          <Text fontSize="sm" fontWeight="medium">
            Only you can see this
          </Text>
        </Flex>
      )}
    </Card>
  );
};
